import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/networkId/networkId.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as nodesActions from 'data/nodes/nodes.actions';
import { newNetworkId } from 'routes/NetworkId/NetworkId.types';
import { adaptToCreate } from 'data/networkId/networkId.adapter';
import * as types from 'data/networkId/networkId.types';
import { NetworkId } from 'data/networkId/networkId.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import NetworkIdForm from './components/NetworkIdForm';
import networkIdTemplate from '../../templates/networkIdTemplate';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/NetworkId.adapter';
import { getAuditDetailsToSubmit } from '../../utils/NetworkIdDataToSubmit';

interface RenderFormProps {
  section: string
}

type RouterParams = {
  history: string
}

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.NetworkId
}

const mapState = (state: RootState) => ({
  networkIdTemplateDef: adaptTemplateVisibilityMode(networkIdTemplate, 'create'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  nodes: nodesSelectors.getNodes(state),
});

const mapDispatch = {
  createNetworkId: actions.createNetworkId,
  goToNetworkIdList: actions.goToNetworkIdList,
  getNodes: nodesActions.getNodes,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const NetworkIdCreate = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  networkIdTemplateDef,
  goToNetworkIdList,
  createNetworkId,
  getNodes,
  nodes,
}: Props): JSX.Element => {
  const portRangeRef = useRef<Ref>(null);
  const mainRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<NetworkId>(cloneDeep(newNetworkId));
  const [template, setTemplate] = useState(networkIdTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getNodes(deliveryFunctionId);
  }, [deliveryFunctionId, getNodes]);

  useEffect(() => {
    const adaptedTemplate = adaptTemplateOptions(networkIdTemplateDef, nodes);
    setTemplate(adaptedTemplate);
  }, [networkIdTemplateDef, nodes]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();
    // clonedTemplate.main = adaptNetworkIdField(template.main as FormTemplateSection, newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateNetworkIdPayload = () => {
    if (mainRef?.current === undefined || portRangeRef?.current === null) return null;

    if ((!mainRef?.current?.isValid(true, true)) && (!portRangeRef?.current?.isValid(true, true))) {
      return null;
    }

    const mainData = mainRef.current?.getValues();
    const portRangeData = portRangeRef.current.getValues();
    // const networkIdData = formRef.current.getValues();
    const data = { ...mainData, ...portRangeData };
    const payload = {
      data: adaptToCreate(data),
      auditDetails: getAuditDetailsToSubmit(data, null, isAuditEnabled, userName, userId, 'CREATE'),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateNetworkIdPayload();
    if (payload) {
      createNetworkId(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToNetworkIdList(history);

  const renderForm = (section : string) => {
    if (formData && template) {
      return (
        <NetworkIdForm
          ref={section === 'main' ? mainRef : portRangeRef}
          data={formData}
          template={template}
          section={section}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="NetworkIdCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('networkId.create.title'))}
          {renderForm('main')}
          <h2 style={{ fontSize: 20, fontWeight: 600 }}>{t('networkId.section.portRange')}</h2>
          {renderForm('portRange')}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(NetworkIdCreate));
